import React, { useEffect, useState } from 'react';
import isNull from 'lodash/isNull';
import omitBy from 'lodash/omitBy';
import { IVGSForm, Config } from 'obt-common';
import { loadVGSCollectPromise } from 'src/utils';
import differenceBy from 'lodash/differenceBy';
import EventEmitter from 'events';
import ChildrenAggressiveMemo from 'src/v2-components/flights/shared/ChildrenAggressiveMemo';
import { ISubscribeCallback, IVGSFormContextValues } from 'obt-common/src/types/payments/vgs';
import { VGSFormContext } from './context';

interface IProps {
  children?: React.ReactNode;
  paymentSourceIds: string[];
}

type IFormMap = IVGSFormContextValues['formMap'];

async function setupForm(emitEvent: NodeJS.EventEmitter['emit']): Promise<IVGSForm> {
  const vaultId = Config.VITE_VGS_VAULT_ID ?? '';
  const vaultEnvironment = Config.VITE_ENVIRONMENT === 'production' ? 'live' : 'sandbox';

  await loadVGSCollectPromise();
  const vgsForm = window.VGSCollect.create(vaultId, vaultEnvironment, (state) => emitEvent('state', state));
  return vgsForm;
}

async function createOrUpdateFormMap(formMap: IFormMap, allPaymentSourceIds: string[]): Promise<IFormMap> {
  const currentPaymentSourceIds = Object.keys(formMap);
  const paymentSourceIdsToAdd = differenceBy(allPaymentSourceIds, currentPaymentSourceIds);
  const paymentSourceIdsToRemove = differenceBy(currentPaymentSourceIds, allPaymentSourceIds);

  if (paymentSourceIdsToAdd.length === 0 && paymentSourceIdsToRemove.length === 0) {
    return formMap;
  }

  const addedForms = await Promise.all(
    paymentSourceIdsToAdd.map(async (paymentSourceId) => {
      const ee = new EventEmitter();
      const emit = ee.emit.bind(ee);
      const subscribe = (callback: ISubscribeCallback) => {
        ee.on('state', callback);
        return () => {
          ee.off('state', callback);
        };
      };

      const vgsForm = await setupForm(emit);
      return [paymentSourceId, { form: vgsForm, subscribe }];
    }),
  );
  const addedFormsMap = Object.fromEntries(addedForms);

  const removedForms = paymentSourceIdsToRemove.map((paymentSourceId) => {
    const currentFormInfo = formMap[paymentSourceId];
    if (currentFormInfo) {
      currentFormInfo.form.unmount();
    }
    return [paymentSourceId, null];
  });
  const removedFormsMap = Object.fromEntries(removedForms);

  const newFormMap = omitBy({ ...formMap, ...addedFormsMap, ...removedFormsMap }, isNull);
  return newFormMap;
}

export const VGSFormProvider: React.FC<IProps> = ({ paymentSourceIds, children }) => {
  const [formMap, setFormMap] = useState<IFormMap>({});
  const formMapRef = React.useRef(formMap);
  formMapRef.current = formMap;

  const validPaymentSourceIds = paymentSourceIds.filter((id): id is string => !!id);

  // This is done to avoid passing array as a dependency to useEffect. As
  // getPaymentSourceIdsForPaymentDetails returns sorted array of
  // paymentSourceIds, we can safely convert it to string and use it as a
  // dependency.
  const paymentSourceIdsString = validPaymentSourceIds.join(',');

  useEffect(() => {
    createOrUpdateFormMap(formMapRef.current, validPaymentSourceIds).then((newFormMap) => {
      setFormMap(newFormMap);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMapRef, paymentSourceIdsString]);

  return (
    <VGSFormContext.Provider value={{ formMap }}>
      <ChildrenAggressiveMemo>{children}</ChildrenAggressiveMemo>
    </VGSFormContext.Provider>
  );
};
